import { Download, Trash2, Phone, Mail, FileText, ScanEye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatDate, formatMedia } from "@/components/admin/shared";
import { StatusControls } from "@/components/admin/applications/StatusControls";
import { PdfViewer } from "@/components/admin/PdfViewer";

const SmallAction = ({ onClick, testid, icon: Icon, children }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 border border-navy/15 bg-white px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-navy hover:border-gold transition-colors" data-testid={testid}>
    <Icon className="h-3.5 w-3.5" /> {children}
  </button>
);

export const ApplicationDetailDialog = ({ selected, onClose, actions }) => (
  <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-xl" data-testid="application-detail-dialog">
      {selected && (
        <>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{selected.nome}</DialogTitle>
            <DialogDescription>{formatDate(selected.created_at)} — {selected.tipologia}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 text-navy hover:text-gold-dark" data-testid="app-detail-email"><Mail className="h-4 w-4 text-gold-dark" />{selected.email}</a>
              <a href={`tel:${selected.telefono}`} className="inline-flex items-center gap-2 text-navy hover:text-gold-dark" data-testid="app-detail-phone"><Phone className="h-4 w-4 text-gold-dark" />{selected.telefono}</a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Università</p>
                <p className="mt-1 text-navy">{selected.universita}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Media ponderata</p>
                <p className={`mt-1 font-serif text-2xl ${selected.media >= 27 ? "text-emerald-700" : "text-navy"}`}>{formatMedia(selected.media)} / 30</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Messaggio di presentazione</p>
              <p className="mt-1 whitespace-pre-wrap leading-relaxed text-slate-600">{selected.messaggio}</p>
            </div>
            <div className="flex items-center justify-between gap-3 border border-navy/15 bg-pearl px-4 py-3">
              <span className="flex min-w-0 items-center gap-3">
                <FileText className="h-5 w-5 shrink-0 text-gold-dark" />
                <span className="truncate font-medium text-navy">{selected.cv_filename}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <SmallAction onClick={() => actions.preview(selected)} testid="app-detail-preview-cv" icon={ScanEye}>Anteprima</SmallAction>
                <SmallAction onClick={() => actions.download(selected)} testid="app-detail-download-cv" icon={Download}>Scarica</SmallAction>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-navy/10 pt-4">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Esito</Label>
              <StatusControls app={selected} onChange={actions.changeStatus} size="lg" />
              <Button variant="outline" size="sm" onClick={() => actions.remove(selected.id)} data-testid="app-detail-delete-button" className="ml-auto text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /> Elimina</Button>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const CvPreviewDialog = ({ preview, onClose, onDownload }) => (
  <Dialog open={!!preview} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="flex h-[92vh] max-w-5xl flex-col gap-0 p-0" data-testid="cv-preview-dialog">
      {preview && (
        <>
          <DialogHeader className="border-b border-navy/10 px-6 py-4">
            <DialogTitle className="font-serif text-xl text-navy">CV — {preview.app.nome}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="truncate">{preview.app.cv_filename}</span>
              <button onClick={() => onDownload(preview.app)} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-navy hover:text-gold-dark" data-testid="cv-preview-download"><Download className="h-3.5 w-3.5" /> Scarica</button>
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 bg-slate-100">
            {!preview.isPdf ? (
              <div className="flex h-full flex-col items-center justify-center gap-4 p-10 text-center" data-testid="cv-preview-unsupported">
                <FileText className="h-12 w-12 text-slate-400" />
                <p className="max-w-md text-sm text-slate-600">L'anteprima è disponibile solo per i file PDF. Questo CV è in formato Word: scaricalo per visualizzarlo.</p>
                <Button onClick={() => onDownload(preview.app)} className="bg-navy text-white hover:bg-navy-800"><Download className="h-4 w-4" /> Scarica CV</Button>
              </div>
            ) : preview.loading ? (
              <div className="flex h-full items-center justify-center" data-testid="cv-preview-loading"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>
            ) : (
              <PdfViewer data={preview.data} />
            )}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
